/**
 * ChangeEmailScreen - move the account to a new address, verified by code.
 *
 * Two internal steps. Step `request` collects the new address and the CURRENT
 * password (re-entered, not trusted from the 30-day JWT) and calls
 * requestEmailChange, which returns 202 with the masked address and changes
 * nothing yet. Step `confirm` redeems the emailed code via confirmEmailChange,
 * which returns a fresh token because the backend bumped token_version.
 *
 * On success the new token MUST be stored before anything else reads it, then
 * refreshAuth() resyncs the context — otherwise the next request uses the now-dead
 * old token, 401s, and the expiration guard logs the user out on the device that
 * just changed its own email.
 */

import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { CodeInput } from '../../components/ui/CodeInput';
import { requestEmailChange, confirmEmailChange } from '../../services/userApi';
import { storeToken } from '../../services/tokenStorage';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResendCooldown } from '../../hooks/useResendCooldown';
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage';
import { RequestError } from '../../utils/apiError';
import { isValidEmail } from '../../utils/validation';
import { showError, showSuccess, showInfo } from '../../utils/toast';

export function ChangeEmailScreen() {
  const navigation = useNavigation();

  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | undefined>();

  const { refreshAuth } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const getApiErrorMessage = useApiErrorMessage();
  const { secondsLeft, start, isCoolingDown } = useResendCooldown();

  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const adoptCooldown = (error: unknown) => {
    if (error instanceof RequestError && error.retryAfterSeconds !== undefined) {
      start(error.retryAfterSeconds);
    }
  };

  const sendCode = async () => {
    setSubmitting(true);
    try {
      const response = await requestEmailChange({
        new_email: newEmail,
        current_password: currentPassword,
      });
      start(response.resend_available_in_seconds);
      setMaskedEmail(response.masked_new_email);
      showInfo(t('settings.emailChangeRequested', { email: response.masked_new_email }));
      return true;
    } catch (error) {
      adoptCooldown(error);
      setFieldError(getApiErrorMessage(error, 'common.error'));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequest = async () => {
    if (!isValidEmail(newEmail)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setFieldError(undefined);
    const ok = await sendCode();
    if (ok) {
      setStep('confirm');
    }
  };

  const handleConfirm = async () => {
    if (code.length !== 6) {
      setFieldError(t('auth.enterCode'));
      return;
    }
    setSubmitting(true);
    setFieldError(undefined);
    try {
      const response = await confirmEmailChange(code);
      // Store the fresh token FIRST: the old one is dead the moment the backend
      // bumped token_version, and any request that races ahead with it would 401.
      await storeToken(response.access_token);
      await refreshAuth();
      showSuccess(t('settings.emailChanged'));
      navigation.goBack();
    } catch (error) {
      setFieldError(getApiErrorMessage(error, 'common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // No separate resend endpoint: re-requesting expires the prior code, so resend
  // is just sendCode again with the retained address and password.
  const handleResend = async () => {
    setFieldError(undefined);
    await sendCode();
  };

  return (
    <View style={styles.container}>
      {/* Unlike ProfileScreen (root of the Settings stack), this screen is
          PUSHED onto that stack, so its own goBack pops back to Profile. */}
      <View style={styles.headerRow}>
        <Pressable
          testID="settings-back"
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel={t('settings.backHeader')}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={styles.backButton}
        >
          <Text style={styles.backChevron}>‹</Text>
        </Pressable>
        <Text style={styles.headerTitle}>{t('settings.changeEmail')}</Text>
      </View>
      <Text style={styles.subtitle}>
        {step === 'request'
          ? t('settings.changeEmailSubtitle')
          : t('settings.emailChangeRequested', { email: maskedEmail })}
      </Text>

      <View style={styles.form}>
        {step === 'request' ? (
          <>
            <Input
              label={t('settings.newEmail')}
              value={newEmail}
              onChangeText={setNewEmail}
              placeholder={t('settings.enterNewEmail')}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <Input
              label={t('settings.currentPassword')}
              value={currentPassword}
              onChangeText={setCurrentPassword}
              placeholder={t('settings.enterCurrentPassword')}
              secureTextEntry
            />
            {fieldError && <Text style={styles.errorText}>{fieldError}</Text>}
            <Button
              title={t('settings.sendCode')}
              onPress={handleRequest}
              loading={submitting}
              style={styles.primaryButton}
            />
          </>
        ) : (
          <>
            <CodeInput
              value={code}
              onChangeText={setCode}
              label={t('auth.codeLabel')}
              autoFocus
              testID="code-input"
            />
            {fieldError && <Text style={styles.errorText}>{fieldError}</Text>}
            <Button
              title={t('settings.confirmChange')}
              onPress={handleConfirm}
              loading={submitting}
              disabled={code.length !== 6}
              style={styles.primaryButton}
            />
            <Button
              title={
                isCoolingDown ? t('auth.resendIn', { seconds: secondsLeft }) : t('auth.resendCode')
              }
              onPress={handleResend}
              variant="secondary"
              loading={submitting}
              disabled={isCoolingDown || submitting}
              style={styles.secondaryButton}
            />
          </>
        )}
      </View>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      padding: 20,
      backgroundColor: theme.colors.background,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 24,
      marginBottom: 8,
    },
    backButton: {
      paddingRight: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
    },
    backChevron: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    headerTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    subtitle: {
      fontSize: 16,
      color: theme.colors.textSecondary,
      marginBottom: 32,
    },
    form: {
      width: '100%',
    },
    primaryButton: {
      marginTop: 8,
    },
    secondaryButton: {
      marginTop: 12,
    },
    errorText: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.error,
      marginBottom: 8,
    },
  });
}
